// Trading mode (SM-441, SM-396 phase 06a).
//
// Every market is one shared logical market with up to two TRADING
// CHANNELS — separate books, quotes, positions and settlement per mode,
// while the content (title, rules, comments, outcomes, resolution) stays
// on the logical market and is identical in both.
//
//	CASH — the app as it has always been. Debits and pays GOLD.
//	GAME — debits RED on buy, credits GREEN on sell / redeem / payout.
//
// The two can never be summed and can never cross-match. A page shows
// one mode at a time, and no number anywhere is a total across both.

/** The canonical enum. Mirrors `contracts/vocab.TradingMode`. */
export type TradingMode = "CASH" | "GAME";

export const MODE_CASH = "CASH" as const;
export const MODE_GAME = "GAME" as const;

/** Display order wherever both appear (the switch, tests, storybook). */
export const TRADING_MODES: readonly TradingMode[] = [MODE_CASH, MODE_GAME];

/**
 * Parse a canonical mode. Fail-closed, exactly like the Go side: the
 * empty string and the legacy value `REAL` return null, because internal
 * code must be explicit. Use {@link normalizeMode} at a boundary.
 */
export function parseMode(raw: unknown): TradingMode | null {
  if (typeof raw !== "string") return null;
  switch (raw.trim().toUpperCase()) {
    case MODE_CASH:
      return MODE_CASH;
    case MODE_GAME:
      return MODE_GAME;
    default:
      return null;
  }
}

/**
 * Resolve a mode at a COMPATIBILITY BOUNDARY — a URL the user pasted, a
 * stored preference written by an older build, a legacy link. Absent
 * resolves to CASH (G3) and the retired value `REAL` resolves to CASH
 * (G4); anything else is still rejected, so a typo lands on CASH rather
 * than on the wrong book.
 *
 * Every unrecognised input therefore ends up on CASH — which is both the
 * safe default (it is the app that already exists) and the reason no
 * caller needs a null branch.
 */
export function normalizeMode(raw: unknown): TradingMode {
  if (typeof raw === "string" && raw.trim().toUpperCase() === "REAL") return MODE_CASH;
  return parseMode(raw) ?? MODE_CASH;
}

/** The other mode. */
export function otherMode(mode: TradingMode): TradingMode {
  return mode === MODE_GAME ? MODE_CASH : MODE_GAME;
}

/**
 * The currencies a mode may show. Deliberately a function of the mode
 * alone and never of a balance, a colour or a name: a GAME page that
 * rendered a GOLD figure would be mixing two ledgers on one screen.
 *
 * GOLD is absent from GAME on purpose. It appears on a GAME surface in
 * exactly one place — inside an explicit GREEN→GOLD conversion quote or
 * result — and never in a GAME total.
 */
export function currenciesForMode(mode: TradingMode): readonly ("RED" | "GREEN" | "GOLD")[] {
  return mode === MODE_GAME ? (["RED", "GREEN"] as const) : (["GOLD"] as const);
}

/**
 * The currency a mode SPENDS — what a buy debits, and therefore what a
 * price, a cost estimate or an order total is denominated in.
 */
export function spendCurrencyForMode(mode: TradingMode): "RED" | "GOLD" {
  return mode === MODE_GAME ? "RED" : "GOLD";
}

/**
 * The currency a mode PAYS OUT — what a sell, a redeem and a resolution
 * payout credit. In GAME this differs from the spend currency, which is
 * the single most misleading thing about the mode if a surface gets it
 * wrong: a GAME redeem credits GREEN, never RED and never GOLD.
 */
export function payoutCurrencyForMode(mode: TradingMode): "GREEN" | "GOLD" {
  return mode === MODE_GAME ? "GREEN" : "GOLD";
}

/**
 * One trading channel of a logical market. Mirrors
 * `contracts/httpapi/v1.TradingChannelItem`, served through the gateway
 * at `GET /api/markets/{id}/channels` (SM-442).
 *
 * `channel_id` is opaque. For CASH it happens to equal the market id;
 * for GAME it is a value only market-service can mint. Never derive one
 * from the other — subscribe to what the server returned.
 */
export interface TradingChannelItem {
  channel_id: string;
  logical_market_id: string;
  trading_mode: TradingMode;
  enabled: boolean;
  /**
   * The folded verdict: `event.<mode>_enabled AND market.<mode>_enabled`,
   * and for GAME the F5 `game.visibility` flag on top. This — not
   * `enabled` — is what decides whether a mode may be offered, because
   * the flag is not visible to the browser any other way.
   */
  effective_enabled: boolean;
  effective_published?: boolean;
  currency_policy_version?: number;
}

export interface TradingChannelListResponse {
  channels: TradingChannelItem[];
}

/** The order-service initiation verdict. Kept separate from channel
 * visibility because F5 may expose a GAME book while F6 still blocks
 * every new GAME order. */
export interface TradingCapabilityItem {
  trading_mode: TradingMode;
  tradable: boolean;
  blocked_at?: "feature_flag" | string;
  code?: string;
  reason?: string;
}

export interface TradingCapabilitiesResponse {
  capabilities: TradingCapabilityItem[];
}
